import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, CreditCard, Plus, Trash2, Edit, CheckCircle, Send } from 'lucide-react';
import { usePi } from '../../contexts/PiContext';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';

interface SavedPaymentMethod {
  id: string;
  type: 'pi' | 'paypal' | 'googlepay' | 'applepay' | 'samsungpay';
  name: string;
  icon: string;
  details: string;
  isDefault: boolean;
  isActive: boolean;
}

const PaymentMethodsPage: React.FC = () => {
  const navigate = useNavigate();
  const { piBalance } = usePi();
  
  const [savedMethods, setSavedMethods] = useState<SavedPaymentMethod[]>([
    {
      id: '1',
      type: 'pi',
      name: 'Pi Network',
      icon: '🥧',
      details: `Balance: π ${piBalance.toFixed(2)}`,
      isDefault: true,
      isActive: true
    },
    {
      id: '2',
      type: 'paypal',
      name: 'PayPal',
      icon: '💙',
      details: 'user@example.com',
      isDefault: false,
      isActive: true
    }
  ]);

  const availableMethods = [
    {
      type: 'googlepay',
      name: 'Google Pay',
      icon: '🟢',
      description: 'Pay with Google Pay'
    },
    {
      type: 'applepay',
      name: 'Apple Pay',
      icon: '🍎',
      description: 'Pay with Apple Pay'
    },
    {
      type: 'samsungpay',
      name: 'Samsung Pay',
      icon: '📱',
      description: 'Pay with Samsung Pay'
    }
  ];

  const handleSetDefault = (id: string) => {
    setSavedMethods(methods =>
      methods.map(method => ({
        ...method,
        isDefault: method.id === id
      }))
    );
  };

  const handleRemoveMethod = (id: string) => {
    setSavedMethods(methods => methods.filter(method => method.id !== id));
  };

  const handleAddMethod = (type: string) => {
    navigate(`/payment-methods/add/${type}`);
  };

  const handlePayPalAction = (method: SavedPaymentMethod) => {
    if (method.type === 'paypal') {
      // Open PayPal in new window
      const paypalUrl = 'https://www.paypal.com/signin';
      window.open(paypalUrl, '_blank', 'width=800,height=600,scrollbars=yes,resizable=yes');
      
      // Show success message after a delay (simulating login)
      setTimeout(() => {
        alert('PayPal account connected successfully! You can now use PayPal for payments and money transfers.');
      }, 2000);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="p-4 flex items-center gap-3">
        <button
          onClick={() => navigate('/settings')}
          className="p-2 text-white hover:bg-white/10 rounded-full transition-colors"
        >
          <ArrowLeft size={20} />
        </button>
        <h1 className="text-2xl font-bold text-white">Payment Methods</h1>
      </div>

      <div className="px-4 pb-8 space-y-6">
        {/* Quick Actions */}
        <div className="grid grid-cols-2 gap-3">
          <Button
            onClick={() => navigate('/payment-methods/send')}
            variant="gradient"
            size="lg"
            className="flex items-center justify-center gap-2"
          >
            <Send size={20} />
            Send Money
          </Button>
          <Button
            onClick={() => navigate('/payment-methods/transactions')}
            variant="outline"
            size="lg"
            className="text-white border-white hover:bg-white hover:text-purple-600 flex items-center justify-center gap-2"
          >
            <CreditCard size={20} />
            History
          </Button>
        </div>

        {/* Saved Payment Methods */}
        <div>
          <h2 className="text-lg font-semibold text-white mb-3">Your Payment Methods</h2>
          <div className="space-y-3">
            {savedMethods.map((method) => (
              <Card key={method.id} className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="text-2xl">{method.icon}</div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-gray-800 dark:text-gray-100">{method.name}</h3>
                        {method.isDefault && (
                          <span className="px-2 py-1 bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 text-xs rounded-full">
                            Default
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-300">{method.details}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {method.type === 'paypal' && (
                      <button
                        onClick={() => handlePayPalAction(method)}
                        className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded-full transition-colors"
                        title="Open PayPal"
                      >
                        Open PayPal
                      </button>
                    )}
                    {!method.isDefault && (
                      <button
                        onClick={() => handleSetDefault(method.id)}
                        className="p-2 text-gray-400 hover:text-green-600 transition-colors"
                        title="Set as default"
                      >
                        <CheckCircle size={16} />
                      </button>
                    )}
                    <button
                      onClick={() => console.log('Edit method', method.id)}
                      className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                      title="Edit"
                    >
                      <Edit size={16} />
                    </button>
                    {method.type !== 'pi' && (
                      <button
                        onClick={() => handleRemoveMethod(method.id)}
                        className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                        title="Remove"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>

        {/* Add New Payment Method */}
        <div>
          <h2 className="text-lg font-semibold text-white mb-3">Add Payment Method</h2>
          <div className="space-y-3">
            {availableMethods.map((method) => (
              <Card
                key={method.type}
                onClick={() => handleAddMethod(method.type)}
                className="p-4 cursor-pointer hover:shadow-lg transition-all duration-200"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="text-2xl">{method.icon}</div>
                    <div>
                      <h3 className="font-semibold text-gray-800 dark:text-gray-100">{method.name}</h3>
                      <p className="text-sm text-gray-600 dark:text-gray-300">{method.description}</p>
                    </div>
                  </div>
                  <Plus size={20} className="text-purple-600 dark:text-purple-400" />
                </div>
              </Card>
            ))}
          </div>
        </div>

        {/* Payment Info */}
        <Card className="p-4 bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-700">
          <div className="flex items-start gap-3">
            <CreditCard size={20} className="text-blue-600 dark:text-blue-400 mt-0.5" />
            <div>
              <h3 className="font-semibold text-blue-800 dark:text-blue-200 mb-1">Secure Payments</h3>
              <p className="text-sm text-blue-700 dark:text-blue-300 mb-2">
                All payment methods are encrypted and secure. Your financial information is protected with industry-standard security.
              </p>
              <div className="space-y-1 text-xs text-blue-600 dark:text-blue-400">
                <p>• Pi Network payments are processed through official Pi SDK</p>
                <p>• PayPal integration with secure OAuth authentication</p>
                <p>• All transactions are encrypted end-to-end</p>
                <p>• Payment data is never stored on our servers</p>
                <p>• Refunds processed according to payment provider policies</p>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default PaymentMethodsPage;